import { inject, Injectable, OnDestroy } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import { from, Observable, Subject } from 'rxjs';
import { AiAgentStreamItem } from './ai-agent-stream-item.models';
import { HubConnectionState as ChatConnectionState } from './hub-connection-state';
import { IdentityService } from '../../core/auth/identity.service';
import { CountryDomainService } from '../../core/country/country-domain.service';

@Injectable({ providedIn: 'root' })
export class AiAgentChatSignalrService implements OnDestroy {
    private readonly identityService = inject(IdentityService);
    private readonly countryDomainService = inject(CountryDomainService);

    private connection: HubConnection | null = null;
    private readonly connectionStateSubject = new Subject<ChatConnectionState>();

    readonly connectionState$: Observable<ChatConnectionState> = this.connectionStateSubject.asObservable();

    connect(): void {
        if (this.connection) return;

        const url = this.resolveHubUrl();
        this.connection = new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: () => this.identityService.currentState.accessToken ?? '',
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();

        this.connection.onreconnecting(() => this.connectionStateSubject.next('reconnecting'));
        this.connection.onreconnected(() => this.connectionStateSubject.next('connected'));
        this.connection.onclose((err) => {
            if (err) console.error('[AiAgentChatSignalrService] connection closed with error', err);
            this.connection = null;
            this.connectionStateSubject.next('disconnected');
        });

        this.connectionStateSubject.next('connecting');
        this.connection.start()
            .then(() => this.connectionStateSubject.next('connected'))
            .catch((err: unknown) => {
                console.error('[AiAgentChatSignalrService] connection failed', err);
                this.connection = null;
                this.connectionStateSubject.next('disconnected');
            });
    }

    disconnect(): void {
        const conn = this.connection;
        this.connection = null;
        conn?.stop();
    }

    getConversationStream(conversationId: string): Observable<AiAgentStreamItem> {
        const conn = this.connection;
        if (!conn || conn.state !== HubConnectionState.Connected) {
            return new Observable(observer => observer.error(new Error('SignalR not connected')));
        }

        return new Observable<AiAgentStreamItem>(observer => {
            const subscription = conn.stream<AiAgentStreamItem>('ConversationStream', conversationId);
            const sub = subscription.subscribe({
                next: item => observer.next(item),
                error: err => observer.error(err),
                complete: () => observer.complete(),
            });
            return () => sub.dispose();
        });
    }

    sendMessage(conversationId: string, message: string): Observable<void> {
        // The hub takes a per-message reply language for the customer-facing agent; the admin agent
        // ignores it. SignalR hub methods have no optional parameters, so the argument is passed
        // explicitly as null rather than omitted — omitting it fails argument binding server-side.
        return from(this.connection?.invoke('SendMessage', conversationId, message, null) ?? Promise.resolve());
    }

    clearConversation(conversationId: string): Observable<void> {
        return from(this.connection?.invoke('ClearConversation', conversationId) ?? Promise.resolve());
    }

    confirmAction(conversationId: string, actionId: string, confirmed: boolean): Observable<void> {
        return from(this.connection?.invoke('ConfirmAction', conversationId, actionId, confirmed) ?? Promise.resolve());
    }

    ngOnDestroy(): void {
        this.disconnect();
    }

    private resolveHubUrl(): string {
        return `${this.countryDomainService.apiBaseUrl()}/admin-ai-agent/hubs/admin-agent`;
    }
}
